import { useAtomValue } from 'jotai';
import { loggedInAdvocateAtom, memberInContextAtom } from '@src/global-state';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import React , { useEffect,useState } from 'react';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { API } from '@src/api';
import { BenefitPlanCategory, ApiResponse } from '@src/api.types';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Button } from '@repo/ui';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { V1Table as Table } from '@uhg-abyss/web/ui/Table';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { Text } from '@uhg-abyss/web/ui/Text';

export type BenefitReferralFormData = {
  advocateFirstName: string;
  advocateLastName: string;
  advocateEmail: string;
  memberFirstName: string;
  memberLastName: string;
  'benefits-radio': string;
};

type BenefitReferralFormState = ReturnType<
  typeof useForm<BenefitReferralFormData>
>;
export type Need = {
  needName:string;
  needDescription:string;
}
export const LogABenefitModal = ({
  onClose,
  isOpen,
  defaultBenefitName,
  setBenefitReferralResponse,
}: {
  onClose: () => void;
  isOpen: boolean;
  defaultBenefitName?: string;
  setBenefitReferralResponse: (resp: { status?: 'success' | 'error'; data?: undefined; error?: string; message?: string } | null | undefined) => void;
}) => {
  const needs:Need[] = [
  {needName:"Food", needDescription:"Food assistance"},
  {needName:"Clothing", needDescription:"Lack of adequate clothing"},
  {needName:"Utilities", needDescription:"Inadequate Housing Utilities"},
  {needName:"Transportation", needDescription:"Transportation insecurity"},
  {needName:"Social Support", needDescription:"Inadequate social interaction"},
  {needName:"Housing", needDescription:"Inadequate housing"},
  {needName:"Internet", needDescription:"Do not have access to the internet"},
  {needName:"Computer/Tablet", needDescription:"Lack of access to computer or laptop"},
  {needName:"Work", needDescription:"Unemployment, unspecified"},
  {needName:"Legal", needDescription:"Problems related to other legal circumstances"},
  {needName:"Medical Cost", needDescription:"Unable to pay for medical care"},
  {needName:"Phone", needDescription:"Unable to pay for phone"},
  {needName:"Social Environment", needDescription:"Other specified problems related to psychosocial circumstances"},
  {needName:"Justice System Support", needDescription:"Imprisonment and other incarceration"},
  {needName:"Family Support", needDescription:"Problem related to primary support group, unspecified"},
  {needName:"Health", needDescription:"Unable to pay for prescriptions"},
  {needName:"Education", needDescription:"Problems related to education and literacy, unspecified"},
  {needName:"Employment", needDescription:"Other problems related to employment"},
  ];
  const loggedInAdvocate = useAtomValue(loggedInAdvocateAtom);
  const memberInContext = useAtomValue(memberInContextAtom);
  const [planBenefitsCategories, setPlanBenefitsCategories] = useState<
    BenefitPlanCategory[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    async function fetchBenefitPlan(): Promise<void> {
      const response = await API.getBenefitPlan(
        memberInContext?.benefitPlanID!,
        memberInContext?.benefitPlanPlatform!
      );

      if (
              response &&
              'data' in response &&
              response.data &&
              'categories' in response.data
            ) {
              setPlanBenefitsCategories(response.data.categories as BenefitPlanCategory[]);
            }
    }
    fetchBenefitPlan();
  }, [memberInContext]);
  const benefitsNames: string[] = planBenefitsCategories.flatMap((category) => {
    return category.benefits.map((benefit) => {
      return benefit.name;
    });
  });
  const benefitReferralForm = useForm<BenefitReferralFormData>({
    defaultValues: {
      advocateFirstName: loggedInAdvocate?.first_name || '',
      advocateLastName: loggedInAdvocate?.last_name || '',
      advocateEmail: loggedInAdvocate?.email || '',
      memberFirstName: memberInContext?.first_name || '',
      memberLastName:
        memberInContext?.last_name
          .split(/(?<=\s|-)/)
          .map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1);
          })
          .join('') || '',
      'benefits-radio': defaultBenefitName || '',
      'needs-radio': '',
    },
  });
  return(
    <React.Fragment>
    <ModalDialog
    size={'1200px'}
        header="Log a Benefit"
        isOpen={isOpen}
        onClose={onClose}
        css={{
          'abyss-modal-content-container': {
            padding: '8px',
          },
        }}
      >
         <FormProvider state={benefitReferralForm}>
        <AdvocateInfoSection/>
          <PaddedLineBreak />
          <MemberInfoSection
            referralForm={benefitReferralForm}
          />
          <PaddedLineBreak/>
          <div>
            <RadioGroup label='Benefit' model='benefits-radio' css={{ 'abyss-radio-group-label': { fontWeight: 'bold' } }}>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {benefitsNames.map((benefitName, index) => (
                  <div key={index} style={{ flex: '0 0 50%', padding: '5px' }}>
                    <RadioGroup.Radio label={benefitName} value={benefitName} />
                  </div>
                ))}
              </div>
            </RadioGroup>
              {benefitReferralForm.getValues('benefits-radio')==='Value Added Services' && (
                <div>
                  <TextInput label='Value added services' validators={{ required: true }} isRequired/>
                  If you have selected value added services, please include the name of the program here.
                </div>
              )}
              <RadioGroup label='Please select the need that is the most relevant to the benefit you have selected' model='needs-radio' validators={{ required: true }} css={{ 'abyss-radio-group-label': { fontWeight: 'bold' }, 'abyss-radio-group-container': { width: '100%' } }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px', border: '1px solid #CBCCCD', borderRadius: '8px', overflow: 'hidden' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', backgroundColor: '#F3F3F3', borderBottom: '1px solid #CBCCCD', fontWeight: 'bold', borderRight: '1px solid #CBCCCD' }}>
                        Need
                      </th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', backgroundColor: '#F3F3F3', borderBottom: '1px solid #CBCCCD', fontWeight: 'bold' }}>
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {needs.map((need, i) => (
                      <tr key={need.needName}>
                        <td style={{ padding: '6px 8px', borderRight: '1px solid #CBCCCD', whiteSpace: 'nowrap', verticalAlign: 'middle', ...(i < needs.length - 1 && { borderBottom: '1px solid #CBCCCD' }) }}>
                          <RadioGroup.Radio label={need.needName} value={need.needName} />
                        </td>
                        <td style={{ padding: '6px 8px', verticalAlign: 'middle', ...(i < needs.length - 1 && { borderBottom: '1px solid #CBCCCD' }) }}>
                          {need.needDescription}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </RadioGroup>
          </div>
          <PaddedLineBreak/>
          <Flex justify='end' style={{gap:'10px'}}>
            <Button variant="outline"
            onClick={onClose}
>
              Cancel
            </Button>
          <Button data-cy='benefit-referral-form-submit-button'
          isDisabled={!benefitReferralForm.formState.isValid || !benefitReferralForm.getValues('benefits-radio') || isLoading}
              type="submit" onClick={async () => {
                setIsLoading(true);

                const response = await API.submitBenefitReferral({
                    "record": {
                          "referralFulfillmentProgramName": benefitReferralForm.getValues()['benefits-radio'],
                          "refFulProgramSubType":benefitReferralForm.getValues()['needs-radio'],
                          },
                          "memberDetails": {
                          "nameFirst": memberInContext?.first_name || '',
                          "nameLast": memberInContext?.last_name || '',
                          "id": memberInContext?.member_id || '',
                          "dateOfBirth": memberInContext?.DOB || '',
                          "lineOfBusiness": memberInContext?.lineOfBusiness || '',
                          "zipcode": memberInContext?.zipCode || '',
                          "gender": memberInContext?.gender || '',
                          "enterpriseIndividualIdentifier": memberInContext?.enterpriseIndividualIdentifier || '',
                          "consumerId": memberInContext?.ConsumerId || ''
                          },
                            "city":memberInContext?.address?.city || '',
                            "state":memberInContext?.address?.state || '',
                            "address":memberInContext?.address?.homeAddress || ''
                });

                if (response === null || response === undefined) {
                  setBenefitReferralResponse(response);
                } else if (response.status === 'success') {
                  setBenefitReferralResponse(response);
                  onClose();
                } else {
                  setBenefitReferralResponse(response);
                }

                setIsLoading(false);
              }}>
            Refer
          </Button>
          </Flex>
          </FormProvider>
          {isLoading && (
            <div
              style={{
                zIndex: 999,
                backgroundColor: '#FFFFFF',
                opacity: 0.75,
                position: 'fixed',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                margin: 0,
                top: 0,
                left: 0,
              }}
            >
              <LoadingSpinner
                size="xl"
                isLoading={isLoading}
                ariaLoadingLabel="Submitting benefit referral..."
              />
            </div>
          )}
      </ModalDialog>
      </React.Fragment>
  );
};

const AdvocateInfoSection = () => {
  return (
    <div>
      <Layout.Group grow>
        <TextInput
          label="Your First Name"
          model="advocateFirstName"
          validators={{ required: true }}
          readOnly
          isDisabled
          isRequired
        />

        <TextInput
          label="Your Last Name"
          model="advocateLastName"
          validators={{ required: true }}
          readOnly
          isDisabled
          isRequired
        />
      </Layout.Group>

      <Layout.Group
        grow
        style={{
          paddingTop: '4px',
        }}
      >
        <TextInput
          label="E-mail"
          model="advocateEmail"
          validators={{ required: true }}
          readOnly
          isDisabled
          isRequired
          data-cy="benefit-referral-form-advocate-email"
        />
      </Layout.Group>
    </div>
  );
};

const MemberInfoSection = ({
  referralForm,
}: {
  referralForm: BenefitReferralFormState;
}) => {
  const handleFirstNameValidation = () => {
    referralForm.trigger('memberFirstName');
  };
  const handleLastNameValidation = () => {
    referralForm.trigger('memberLastName');
  };
  return (
    <FormProvider state={referralForm}>
      <div>
        <Layout.Group grow>
          <TextInput
            label="Member's First Name"
            model="memberFirstName"
            onChange={handleFirstNameValidation}
            onBlur={handleFirstNameValidation}
            validators={{ required: true }}
            isRequired
            data-cy="benefit-referral-form-member-first-name"
          />

          <TextInput
            label="Member's Last Name"
            model="memberLastName"
            onChange={handleLastNameValidation}
            onBlur={handleLastNameValidation}
            validators={{ required: true }}
            isRequired
            data-cy="benefit-referral-form-member-last-name"
          />
        </Layout.Group>
      </div>
    </FormProvider>
  );
};
const PaddedLineBreak = () => {return (
  <hr
    style={{
      marginLeft: '16px',
      marginRight: '16px',
      marginTop: '24px',
      marginBottom: '16px',
    }}
  />
)};
